import React from 'react'
import { Button, Form, FormTextInput, ImageModal } from '@superhi/design'
import { useMutation } from '@apollo/client'
import { string } from 'yup'
import { setErrorsFromApollo } from '@superhi/utils'

import { LEAD_CREATE } from './mutations'

import { API, useSession } from 'src/api'

type FormValues = {
  email: string
}

type Props = Omit<
  React.ComponentProps<typeof ImageModal>,
  'portalElementId' | 'title' | 'subtitle' | 'imageFit' | 'imageBackgroundColor'
> & {
  title: string
  subtitle?: string
  successMessage?: string
  downloads: React.ReactElement
  imageFit?: React.ComponentProps<typeof ImageModal>['imageFit']
  imageBackgroundColor?: React.ComponentProps<typeof ImageModal>['imageBackgroundColor']

  leadTag: string
  onEmailEntered?: (email: string) => void
}

const EmailModal: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  subtitle = 'After entering your email address below, you’ll be able to start downloading straight away!',
  downloads,
  image,
  onClose,
  imageFit = 'contain',
  imageBackgroundColor = 'YELLOW_20',
  successMessage = 'Thanks!',

  leadTag,

  onEmailEntered,
}) => {
  const [isLoggedIn] = useSession()
  const [leadCreate, leadCreateMutation] = useMutation<
    API.LibraryGuideLeadCreate,
    API.LibraryGuideLeadCreateVariables
  >(LEAD_CREATE)

  const showForm = !isLoggedIn && !leadCreateMutation.data?.leadCreate

  return (
    <ImageModal
      onClose={onClose}
      title={title}
      subtitle={showForm ? subtitle : successMessage}
      image={image}
      imageBackgroundColor={imageBackgroundColor}
      imageFit={imageFit}
    >
      {showForm ? (
        <Form<FormValues>
          name="email-modal"
          initialValues={{ email: '' }}
          validationSchema={{ email: string().required() }}
          onSubmit={async (values, helpers) => {
            try {
              await leadCreate({
                variables: {
                  email: values.email,
                  tag: leadTag,
                },
              })

              if (onEmailEntered) {
                onEmailEntered(values.email)
              }
            } catch (e) {
              setErrorsFromApollo(helpers.setFieldError, e)
            }
          }}
        >
          <FormTextInput ariaLabel="Email" name="email" placeholder="Your email address" />
        </Form>
      ) : (
        downloads
      )}
    </ImageModal>
  )
}

export default EmailModal
